import {useState, type ChangeEvent} from 'react'
import {useTranslation} from 'react-i18next'

import {InputComponentProps} from './types'
import {
  MEASUREMENT_SPECS,
  MeasurementKind,
  canonicalToPartStrings,
  formatStoredCanonical,
  parseCanonical,
  partsToCanonical,
} from './measurementRegistry'
import {
  PartInputWrapper,
  PartSuffix,
  UnitSelectWrapper,
} from './UnitConvertibleInput.styled'

import {Typography} from '~/components/general/typography/Typography'
import {Input} from '~/components/general/input/Input'
import {Container} from '~/components/general/container/Container'
import {Select, Option} from '~/components/general/select/Select'
import {StyledDashboardInnerCardTableCell} from '~/components/facility/GeneralDataCard.styled'

type UnitConvertibleInputProps = InputComponentProps & {
  measurement: MeasurementKind
  label?: string
}

export const UnitConvertibleInput = ({
  title,
  name,
  initialValue,
  measurement,
  label,
  disabled = false,
  currentData,
  onUpdate,
  required,
}: UnitConvertibleInputProps) => {
  const {t} = useTranslation<string>()
  const spec = MEASUREMENT_SPECS[measurement]
  const [variantId, setVariantId] = useState(spec.defaultVariantId)
  const variant =
    spec.variants.find((item) => item.id === variantId) ?? spec.variants[0]

  const [canonical, setCanonical] = useState<number | undefined>(() =>
    parseCanonical(initialValue),
  )
  const [partValues, setPartValues] = useState<Record<string, string>>(() =>
    canonicalToPartStrings(variant, parseCanonical(initialValue)),
  )

  const isDisabled =
    typeof disabled === 'function' ? disabled(currentData) : disabled
  const displayTitle = label ? t(label) : title
  const hasValue = canonical != null

  const handlePartChange = (key: string, raw: string) => {
    const nextParts = {...partValues, [key]: raw}
    setPartValues(nextParts)

    const nextCanonical = partsToCanonical(variant, nextParts)
    setCanonical(nextCanonical)

    if (nextCanonical == null) {
      onUpdate([{name, value: undefined}])
      return
    }

    onUpdate([
      {name, value: formatStoredCanonical(nextCanonical, spec.storageDecimals)},
    ])
  }

  const handleVariantChange = (nextVariantId: string) => {
    if (nextVariantId === variantId) {
      return
    }
    const nextVariant = spec.variants.find((item) => item.id === nextVariantId)
    if (!nextVariant) {
      return
    }

    setVariantId(nextVariantId)
    setPartValues(canonicalToPartStrings(nextVariant, canonical))
  }

  return (
    <StyledDashboardInnerCardTableCell>
      <Container direction="column">
        <Typography weight="medium" size="sm" required={required} inline>
          {displayTitle}
        </Typography>
        <Container flex align="center" spacing={1} fullWidth>
          {variant.parts.map((part) => (
            <PartInputWrapper
              key={part.key}
              flex
              align="center"
              spacing={0.5}
              fullWidth>
              <Input
                type="number"
                size="small"
                name={`${name}-${part.key}`}
                placeholder={t('Enter value')}
                disabled={isDisabled}
                value={partValues[part.key] ?? ''}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  handlePartChange(part.key, event.target.value)
                }
                required={required && !hasValue}
              />
              {variant.parts.length > 1 && (
                <PartSuffix weight="medium" size="sm">
                  {t(part.suffixKey)}
                </PartSuffix>
              )}
            </PartInputWrapper>
          ))}
          <UnitSelectWrapper>
            <Select
              size="small"
              width={108}
              disabled={isDisabled}
              selectedOption={
                <Typography weight="medium" size="sm" inline>
                  {t(variant.labelKey)}
                </Typography>
              }>
              {spec.variants.map((item) => (
                <Option
                  key={item.id}
                  size="small"
                  onClick={() => handleVariantChange(item.id)}>
                  <Typography weight="medium">{t(item.labelKey)}</Typography>
                </Option>
              ))}
            </Select>
          </UnitSelectWrapper>
        </Container>
      </Container>
    </StyledDashboardInnerCardTableCell>
  )
}
